import React, {useEffect, useRef, useState} from "react";
import {ResponsiveGrid} from "@adobe/aem-react-editable-components";
import {EmbeddedFormWrapper, StickyBar} from "./StickyForm.style";
import Button from "../Button/Button";
import DownloadFormOverlay from "../DownloadFormOverlay/DownloadFormOverlay";
import DownloadForm from "../Forms/DownloadForm/DownloadForm";
import Loading from "../Loading/Loading";
import {useIsMobile} from "../../utils/styled/_mixins.style";
import {sizes} from "../../utils/styled/_variables.styled";
import {CAStates, Countries, USStates,} from "../Forms/DropdownContentLists.json";
import EmbedHtmlMarketo from "../Embed/EmbedHtmlMarketo";
import {ErrorBoundary} from "react-error-boundary";
import {urlHasParam} from "../../utils/js/params";
import { generateHTML } from "../../utils/js/clear-native-marketo-stylesheet";

function StickyForm(props) {
	const {
		stickyFormHtmlId,
		stickyFormPageTitle,
		stickyFormTitle,
		stickyFormDescription,
		buttonText,
		buttonHandle,
		showButtonHandle,
		marketingConsentText,
		cqItems,
		cqItemsOrder,
		cqPath,
		category,
		isInEditor,
		actionPath,
		overlap,
		embedHtml,
		firstNameText,
		lastNameText,
		emailText,
		companyText,
		countryText,
		stateText,
		requiredFirstNameText,
		requiredLastNameText,
		requiredEmailText,
		requiredCompanyText,
		requiredCountry,
		requiredState,
		countryValues,
		usaStateValues,
		canadaStateValues,
		fieldRequired,
		captchaConfig,
		pageLanguage
	} = props;

	const { isMobile } = useIsMobile(sizes.tabletLarge);

	const [contactFormOverlayOpen, setContactFormOverlayOpen] = useState(null);
	const [showLoading, setShowLoading] = useState(false);
	const [showBorder, setShowBorder] = useState(true);
	const [allowSubmit, setAllowSubmit] = useState(false);
	const [formOffset, setFormOffset] = useState(0);
	const [formInitialTop, setFormInitialTop] = useState(0);

	const form = useRef();
	const section = useRef();
	const formScroll = useRef();
	const downloadFormRef = useRef(null);

	const [windowSize, setWindowSize] = useState([
		window.innerWidth,
		window.innerHeight,
	]);

    const containerProperties = {
        title: 'Sticky Container',
        cqItems: cqItems,
        cqItemsOrder: cqItemsOrder,
        cqPath: cqPath
    }

	useEffect(() => {
		const handleScroll = () => {
			const { scrollHeight, clientHeight } = formScroll.current;
			const maxScrollDistance = scrollHeight - clientHeight;

			if(maxScrollDistance <= formScroll.current.scrollTop) {
				setShowBorder(false);
			} else {
				setShowBorder(true);
			}
		};

		const formScrolling = formScroll.current;
		formScrolling.addEventListener('scroll', handleScroll);

		return () => {
			formScrolling.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const unsetGrid = useRef(null);

	useEffect(() => {						
		setFormInitialTop(form.current.getBoundingClientRect().top);
		unsetGrid.current?.parentElement.classList.add(`unset__grid`);
	},[]);

	useEffect(() => {
		setFormOffset(formInitialTop - window.innerHeight + 200);				
	},[windowSize, formInitialTop]);

	useEffect(() => {
		const handleWindowResize = () => {
			setWindowSize([window.innerWidth, window.innerHeight]);
		};

		window.addEventListener('resize', handleWindowResize);
		window.addEventListener('orientationchange', handleWindowResize);
		window.addEventListener('load', handleWindowResize);
		window.addEventListener('reload', handleWindowResize);

		return () => {
			window.removeEventListener('resize', handleWindowResize);
			window.removeEventListener('orientationchange', handleWindowResize);
			window.removeEventListener('load', handleWindowResize);
			window.removeEventListener('reload', handleWindowResize);
		};
	}, []);

	const [triggerFocus, setTriggerFocus] = useState(0);

	useEffect(() => {
		if(contactFormOverlayOpen != null)
			contactFormOverlayOpen ? window.location.href = "#contact-form" : window.location.href = "#stickyform__container";
	}, [contactFormOverlayOpen]);

	useEffect(() => {
		var checkConsentBox = setInterval(function() {
			const consentCheckbox = document.getElementById("consentCheckbox");
		if (consentCheckbox){ 
			clearInterval(checkConsentBox)
			const consentWrapper = consentCheckbox.parentElement.parentElement;
			consentWrapper.classList.add("wrapper__consent")
		}
		}, 200);
	},[])

	function offset(el) {
		var rect = el.getBoundingClientRect(),
		scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
		scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		return { top: rect.top + scrollTop, left: rect.left + scrollLeft }
	}


	const handleStickyButtonClick = () => {
		if(isMobile) return setContactFormOverlayOpen(true);

		if (downloadFormRef.current) {
			downloadFormRef.current.validateRequired();
		}
		document.querySelector(".mktoForm input[type='text']:first-of-type")?.focus();
	};

	const clearNativeMarketoStylesheet = generateHTML(captchaConfig, pageLanguage)

	return (
		<StickyBar ref={unsetGrid} isInEditor={isInEditor} id={stickyFormHtmlId} showBorder={showBorder} overlap={overlap} formOffset={formOffset}  fieldRequired={fieldRequired}>
			<div className="stickyform" ref={section}>
				<div className="stickyform__container" id="stickyform__container">
					<div className="sticky-aem-content">
						<ResponsiveGrid {...containerProperties} />
						{!isMobile && showButtonHandle && <div className="stickyform__handle">
							<Button
								text={buttonHandle}
								disabled={false}
								onClick={() => handleStickyButtonClick()}
								noIcon
								id="mobile-original-focus"
							/>
						</div>}
					</div>
					<div className={`stickyform__content`} ref={form}>
						<div className="stickyform__title hide-desktop">
							<span className="heading-5">{stickyFormTitle}</span>
						</div>
						<div className="stickyform__form" ref={formScroll}>
							<div className="stickyform__title">
								<span className="heading-5">{stickyFormTitle}</span>
							</div>
							<p>{stickyFormDescription}</p>
							{embedHtml ? 
							<EmbeddedFormWrapper>
								{!isMobile && <EmbedHtmlMarketo embedHtml={`${embedHtml}${clearNativeMarketoStylesheet}`}/> }
							</EmbeddedFormWrapper>
							:
								<DownloadForm
									captchaConfig={captchaConfig}
									marketingConsentText={marketingConsentText}
									countryList={Countries}
									USStateList={USStates}
									CAStateList={CAStates}
									buttonText={buttonText}
									ref={downloadFormRef}
									showCheckbox={!!marketingConsentText}
									isDisabled={setAllowSubmit}
									category={category}
									LoadingWatcher={setShowLoading}
									triggerFocus={triggerFocus}
									actionPath={actionPath}
									useAnalytics={!isMobile}
									firstNameText={firstNameText}
									lastNameText={lastNameText}
									emailText={emailText}
									companyText={companyText}
									countryText={countryText}
									stateText={stateText}
									countryValues={countryValues.map((item) => ({label: item.text, value: item.value}))}
									usaStateValues={usaStateValues.map((item) => ({label: item.text, value: item.value}))}
									canadaStateValues={canadaStateValues.map((item) => ({label: item.text, value: item.value}))}
									requiredFirstNameText={requiredFirstNameText}
									requiredLastNameText={requiredLastNameText}
									requiredEmailText={requiredEmailText}
									requiredCompanyText={requiredCompanyText}
									requiredCountry={requiredCountry}
									requiredState={requiredState}
								/>
							}
							{showLoading && <Loading />}
						</div>
						{(isMobile || !embedHtml) &&
							<div className="stickyform__button_overlap">
								<Button
									text={buttonText}
									disabled={false}
									onClick={handleStickyButtonClick}
									noIcon
									id="mobile-original-focus"
								/>
							</div>
						}
					</div>
				</div>
				{isMobile &&
					<DownloadFormOverlay
						open={contactFormOverlayOpen}
						close={() => setContactFormOverlayOpen(false)}
						title={stickyFormPageTitle}
						text={stickyFormDescription}
						subtitle={stickyFormTitle}
						buttonText={buttonText}
						marketingConsentText={marketingConsentText}
						category={category}
						triggerFocus={triggerFocus}
						actionPath={actionPath}
						useAnalytics={isMobile}
						embedHtml={embedHtml ? `${embedHtml}${clearNativeMarketoStylesheet}` : ''}
						firstNameText={firstNameText}
						lastNameText={lastNameText}
						emailText={emailText}
						companyText={companyText}
						countryText={countryText}
						stateText={stateText}
						countryValues={countryValues}
						usaStateValues={usaStateValues}
						canadaStateValues={canadaStateValues}
						requiredFirstNameText={requiredFirstNameText}
						requiredLastNameText={requiredLastNameText}
						requiredEmailText={requiredEmailText}
						requiredCompanyText={requiredCompanyText}
						requiredCountry={requiredCountry}
						requiredState={requiredState}
						pageLanguage={pageLanguage}
						captchaConfig={captchaConfig}
					/>
				}
			</div>
		</StickyBar>
	);
}

const StickyFormHandleErrors = props => {
	return (
		<ErrorBoundary fallback={props.isInEditor || urlHasParam('wcmmode=disabled') ? <div style={{minHeight: '50px', display: 'flex', alignItems: 'center', backgroundColor: '#ffe1db'}}><p>The sticky form component is currently experiencing errors. Please try again later.</p></div> : <div></div>}>
			<StickyForm {...props}/>
		</ErrorBoundary>
	)
  }

export default StickyFormHandleErrors;